import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { TelnyxRTC } from '@telnyx/webrtc';

import { VoiceHttpService } from '../voice-http.service';

export type VoiceWebrtcStatus = 'idle' | 'connecting' | 'ready' | 'active';

/**
 * Thin wrapper around the @telnyx/webrtc SDK so an agent can take a transferred call
 * in the browser. It registers up-front (POST /voice/webrtc/token → SDK login → wait
 * for `telnyx.ready`), so the inbox only enables "pick up" once `registered` is true.
 * On pickup the backend bridges the call and the SDK auto-answers the inbound invite.
 * Remote audio is piped into a hidden <audio> element.
 */
@Injectable({ providedIn: 'root' })
export class VoiceWebrtcClientService {
    private readonly voiceS = inject(VoiceHttpService);

    /** False once the backend reports WebRTC isn't configured (503). */
    readonly available = signal(true);
    readonly status = signal<VoiceWebrtcStatus>('idle');
    readonly muted = signal(false);
    /** True once the SDK is logged in (ready or on a call). Gates the pickup button. */
    readonly registered = computed(
        () => this.status() === 'ready' || this.status() === 'active'
    );
    /** Fires when the active call hangs up (locally or remotely). */
    readonly callEnded$ = new Subject<void>();

    private client?: TelnyxRTC;
    private clientAddress = '';
    private activeCall?: any;
    private renewTimer?: ReturnType<typeof setTimeout>;
    private remoteAudio?: HTMLAudioElement;
    private loginPromise?: Promise<string>;
    private readyWaiters: Array<{ resolve: () => void; reject: (e: unknown) => void }> = [];

    /**
     * The registered SIP URI (sip_address) from the single token mint. This is exactly
     * what /pickup must receive — never re-mint a token at pickup time, or the address
     * would no longer match the SDK's registration and Telnyx would reject the bridge.
     */
    get sipAddress(): string {
        return this.clientAddress;
    }

    /** Register eagerly (fire-and-forget) — called when the agent enters the inbox. */
    register() {
        this.ensureReady().catch(() => undefined);
    }

    /**
     * Prime the microphone permission before answering, so the SDK's own getUserMedia
     * on `call.answer()` succeeds. Resolves false if the user blocked the mic (the
     * browser won't re-prompt a hard denial — they must re-allow it in site settings).
     */
    async ensureMicPermission(): Promise<boolean> {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(t => t.stop());
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Resolve once the SDK is logged in, returning the `client_address` (sip_address)
     * to hand to the pickup call. De-duplicates concurrent logins; sets
     * `available=false` and rejects on a 503.
     */
    ensureReady(): Promise<string> {
        if (this.registered() && this.clientAddress) return Promise.resolve(this.clientAddress);
        if (this.loginPromise) return this.loginPromise;
        this.loginPromise = this.doLogin();
        void this.loginPromise.finally(() => (this.loginPromise = undefined));
        return this.loginPromise;
    }

    mute() {
        this.activeCall?.muteAudio();
        this.muted.set(true);
    }

    unmute() {
        this.activeCall?.unmuteAudio();
        this.muted.set(false);
    }

    toggleMute() {
        this.muted() ? this.unmute() : this.mute();
    }

    hangup() {
        // A bye must ALWAYS go out: if the tracked reference was lost, fall back to
        // whatever call the SDK still holds instead of silently doing nothing.
        const call = this.activeCall ?? this.firstSdkCall();
        try {
            call?.hangup();
        } catch {
            /* leg already dead on the SDK side */
        }
        if (!call) {
            // Nothing live locally — make sure the UI doesn't stay stuck "on call".
            this.activeCall = undefined;
            this.muted.set(false);
            this.status.set(this.client ? 'ready' : 'idle');
            this.callEnded$.next();
        }
    }

    /** First call the SDK still tracks internally (fallback when our reference is lost). */
    private firstSdkCall(): { hangup: () => void } | undefined {
        const calls = (this.client as unknown as { calls?: Record<string, unknown> })?.calls;
        const first = calls ? Object.values(calls)[0] : undefined;
        return first as { hangup: () => void } | undefined;
    }

    /* ── internals ──────────────────────────────────────────────── */

    private async doLogin(): Promise<string> {
        this.status.set('connecting');
        let token;
        try {
            token = await firstValueFrom(this.voiceS.getWebrtcToken$());
        } catch (err) {
            if ((err as { status?: number })?.status === 503) this.available.set(false);
            this.status.set('idle');
            throw err;
        }

        this.clientAddress = token.sip_address;
        this.client = new TelnyxRTC({ login_token: token.token });
        // The Telnyx WebRTC SDK logs every socket/verto message at debug/info via `loglevel`,
        // which floods the console. Drop its logger to `warn` so only real issues surface.
        (
            this.client as unknown as { __logger?: { setLevel?: (level: string) => void } }
        ).__logger?.setLevel?.('warn');
        this.client.remoteElement = this.ensureRemoteAudio();
        this.attachHandlers(this.client);
        this.client.connect();
        this.scheduleRenew(token.expires_in_sec);

        await this.waitForReady();
        return this.clientAddress;
    }

    private attachHandlers(client: TelnyxRTC) {
        client.on('telnyx.ready', () => {
            if (this.status() === 'connecting') this.status.set('ready');
            this.readyWaiters.forEach(w => w.resolve());
            this.readyWaiters = [];
        });
        client.on('telnyx.error', (err: unknown) => {
            this.status.set('idle');
            this.readyWaiters.forEach(w => w.reject(err));
            this.readyWaiters = [];
        });
        client.on('telnyx.notification', (n: { type?: string; call?: any }) => {
            if (n?.type === 'callUpdate' && n.call) this.onCallUpdate(n.call);
        });
    }

    private onCallUpdate(call: any) {
        const state = String(call?.state ?? '');
        const callId = call?.id;
        const trackedId = (this.activeCall as { id?: string } | undefined)?.id;
        const isForeign = this.activeCall != null && trackedId !== callId;
        switch (state) {
            case 'ringing':
                // The backend bridges the transferred leg to us — answer automatically.
                // One call at a time: while live, a second invite (late bridge of a failed
                // pickup attempt…) must NOT hijack `activeCall` — decline it instead, or
                // "hang up" would target the wrong leg and the real call would never get
                // its bye (stuck in_progress on Telnyx).
                if (isForeign && this.status() === 'active') {
                    try {
                        call.hangup();
                    } catch {
                        /* noop */
                    }
                    return;
                }
                this.activeCall = call;
                call.answer();
                break;
            case 'active':
                if (isForeign) return;
                this.activeCall = call;
                this.muted.set(false);
                this.status.set('active');
                break;
            case 'hangup':
            case 'destroy':
                // Only the tracked call ends the session — a dying foreign leg clearing
                // `activeCall` is what turned "Colgar" into a silent no-op.
                if (isForeign) return;
                this.activeCall = undefined;
                this.muted.set(false);
                this.status.set(this.client ? 'ready' : 'idle');
                this.callEnded$.next();
                break;
        }
    }

    private waitForReady(): Promise<void> {
        if (this.status() === 'ready' || this.status() === 'active') return Promise.resolve();
        return new Promise((resolve, reject) => this.readyWaiters.push({ resolve, reject }));
    }

    /** Refresh credentials a bit before they expire; only re-login when idle. */
    private scheduleRenew(expiresInSec: number) {
        if (this.renewTimer) clearTimeout(this.renewTimer);
        const delay = Math.max(30, expiresInSec - 60) * 1000;
        this.renewTimer = setTimeout(() => {
            if (this.status() === 'active') {
                // Don't disrupt a live call; retry shortly after.
                this.scheduleRenew(90);
                return;
            }
            this.teardown();
            this.register();
        }, delay);
    }

    private ensureRemoteAudio(): HTMLAudioElement {
        if (this.remoteAudio) return this.remoteAudio;
        const el = document.createElement('audio');
        el.id = 'voice-webrtc-remote-audio';
        el.autoplay = true;
        el.style.display = 'none';
        document.body.appendChild(el);
        this.remoteAudio = el;
        return el;
    }

    private teardown() {
        try {
            this.client?.disconnect();
        } catch {
            /* noop */
        }
        this.client = undefined;
        this.activeCall = undefined;
        this.clientAddress = '';
        this.status.set('idle');
    }
}
